/* ============================================================================
   MOOJO — Roue chakana : repli scroll, et moteur B2 unique.
   ---------------------------------------------------------------------------
   Instances non-B2 : `animation-timeline: view()` si le navigateur le
   connaît, sinon ce module (Safari). Idempotent : un observateur, un
   écouteur scroll, un rAF.

   B2 (source + complément) : le compositeur CSS ne peut pas exprimer la
   trajectoire à trois points (entrée haute → apparition basse à 0° →
   sortie basse). Un seul moteur JS, dans les trois voies de compatibilité.
   Une lecture géométrique par frame, un angle, deux vues. Le hub
   `.moojo-manifest-gateways` borne la séquence ; il n'est pas le sujet
   d'une ViewTimeline.

     p = 0, −45°  : la tablette haute entre dans le viewport (top = vh)
     p milieu, 0° : le fragment bas commence à apparaître (top = vh)
     p = 1, +45°  : le fragment bas a quitté par le haut (bottom = 0)

   Le complément n'a jamais d'horloge propre. Appariement par
   `data-moojo-groupe`. Éditeur : load / unload / select / reorder.
   ========================================================================== */
(() => {
  'use strict';

  if (window.__moojoWheelLoaded) return;
  window.__moojoWheelLoaded = true;

  const SEL = '.moojo-wheel-band, .moojo-wheel-host';
  const B2_SRC = '.moojo-wheel-band[data-moojo-role="source"][data-moojo-groupe]';
  const B2_CPL = '.moojo-wheel-band[data-moojo-role="complement"][data-moojo-groupe]';
  const HUB_SEL = '.moojo-manifest-gateways';
  const groupe = (b) => b.getAttribute('data-moojo-groupe');

  const supportsTimeline = () =>
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    CSS.supports('animation-timeline', 'view()');

  function paires() {
    const src = new Map();
    for (const b of document.querySelectorAll(B2_SRC))
      if (!src.has(groupe(b))) src.set(groupe(b), b);
    const cpl = new Map();
    for (const b of document.querySelectorAll(B2_CPL)) {
      const g = groupe(b);
      if (!cpl.has(g)) cpl.set(g, []);
      cpl.get(g).push(b);
    }
    return { src, cpl };
  }

  function apparier() {
    const { src, cpl } = paires();
    const motif = document.getElementById('moojo-tablette-motif');
    for (const [g, bandes] of cpl)
      for (const b of bandes) {
        if (src.has(g) && motif) b.removeAttribute('data-moojo-orphelin');
        else b.setAttribute('data-moojo-orphelin', '');
      }
    return { src, cpl, motif };
  }

  function hubEntre(haute, basse) {
    for (const h of document.querySelectorAll(HUB_SEL)) {
      const afterHaute = haute.compareDocumentPosition(h) & Node.DOCUMENT_POSITION_FOLLOWING;
      const beforeBasse = h.compareDocumentPosition(basse) & Node.DOCUMENT_POSITION_FOLLOWING;
      if (afterHaute && beforeBasse) return h;
    }
    return null;
  }

  // T1 = 0° à l'apparition du bas. `window.__moojoB2Entry` sert uniquement
  // à comparer T2 (+15°) sans laisser deux implémentations.
  function entryDeg(haute) {
    if (typeof window.__moojoB2Entry === 'number') return window.__moojoB2Entry;
    const n = parseFloat(haute.getAttribute('data-moojo-entry'));
    return Number.isFinite(n) ? n : 0;
  }

  function angleB2(hauteEl, basseEl, vh) {
    const haute = hauteEl.getBoundingClientRect();
    const basse = basseEl.getBoundingClientRect();
    const turn = parseFloat(hauteEl.dataset.wheelTurn || '90');
    const start = -turn / 2;
    const end = turn / 2;
    const entry = entryDeg(hauteEl);
    const span1 = basse.top - haute.top;
    const u1 = span1 <= 0 ? 1 : (vh - haute.top) / span1;
    if (u1 <= 0) return start;
    if (u1 < 1) return start + (entry - start) * u1;
    const span2 = vh + basse.height;
    const u2 = span2 <= 0 ? 1 : Math.min(1, Math.max(0, (vh - basse.top) / span2));
    return entry + (end - entry) * u2;
  }

  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
  const observed = new Set();
  const live = new Set();
  let frame = null;
  let scrolling = false;
  let b2Listening = false;

  function sequenceProche(hauteEl, basseEl, vh) {
    const margin = vh * 0.8;
    const H = hauteEl.getBoundingClientRect();
    const B = basseEl.getBoundingClientRect();
    return H.top < vh + margin && B.bottom > -margin;
  }

  function paint() {
    frame = null;
    const vh = innerHeight || document.documentElement.clientHeight;
    const { src, cpl } = paires();

    if (!REDUCED.matches) {
      for (const [g, haute] of src) {
        const basses = cpl.get(g) || [];
        if (!basses.length) continue;
        const basse = basses[0];
        const hidden = getComputedStyle(basse).display === 'none';
        if (!hidden && !sequenceProche(haute, basse, vh)) continue;
        let deg;
        if (hidden) {
          const r = haute.getBoundingClientRect();
          const p = Math.min(1, Math.max(0, (vh - r.top) / (vh + r.height)));
          const turn = parseFloat(haute.dataset.wheelTurn || '90');
          deg = ((p - 0.5) * turn).toFixed(2) + 'deg';
          haute.style.setProperty('--moojo-wheel-rot', deg);
        } else {
          deg = angleB2(haute, basse, vh).toFixed(2) + 'deg';
          haute.style.setProperty('--moojo-wheel-rot', deg);
          for (const b of basses) b.style.setProperty('--moojo-wheel-rot', deg);
        }
      }
    }

    for (const band of live) {
      if (band.getAttribute && band.getAttribute('data-moojo-role')) continue;
      if (!band.classList || !band.classList.contains('moojo-wheel-band') && !band.classList.contains('moojo-wheel-host')) continue;
      const r = band.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, (vh - r.top) / (vh + r.height)));
      const turn = parseFloat(band.dataset.wheelTurn || '90');
      band.style.setProperty('--moojo-wheel-rot', ((p - 0.5) * turn).toFixed(2) + 'deg');
    }
  }

  function schedule() {
    if (frame === null) frame = requestAnimationFrame(paint);
  }

  function bindScroll(on) {
    const want = on || b2Listening;
    if (want === scrolling) return;
    scrolling = want;
    if (want) addEventListener('scroll', schedule, { passive: true });
    else removeEventListener('scroll', schedule);
  }

  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (e.isIntersecting) live.add(e.target);
      else live.delete(e.target);
    }
    bindScroll(live.size > 0);
    if (live.size) schedule();
  }, { rootMargin: '80% 0px' });

  addEventListener('resize', schedule, { passive: true });

  function observe(el) {
    if (!el || observed.has(el)) return;
    observed.add(el);
    io.observe(el);
  }

  function forget(el) {
    if (!observed.delete(el)) return;
    io.unobserve(el);
    live.delete(el);
    if (el.style) el.style.removeProperty('--moojo-wheel-rot');
    bindScroll(live.size > 0);
  }

  function figerB2(src, cpl) {
    const g = src ? { src, cpl } : apparier();
    for (const b of [...g.src.values(), ...[].concat(...g.cpl.values())])
      b.style.setProperty('--moojo-wheel-rot', '0deg');
  }

  function initB2() {
    const { src, cpl } = apparier();
    const pairs = [];
    for (const [g, haute] of src) {
      const basses = cpl.get(g) || [];
      if (!basses.length) continue;
      pairs.push([haute, basses]);
    }
    if (!pairs.length) return false;

    for (const [haute, basses] of pairs) {
      haute.classList.add('moojo-wheel-band--js');
      for (const b of basses) b.classList.add('moojo-wheel-band--js');
    }
    if (REDUCED.matches) {
      figerB2(src, cpl);
      return true;
    }
    for (const [haute, basses] of pairs) {
      observe(haute);
      const hub = hubEntre(haute, basses[0]);
      if (hub) observe(hub);
      observe(basses[0]);
    }
    b2Listening = true;
    bindScroll(true);
    schedule();
    return true;
  }

  function initSolo(root) {
    if (supportsTimeline()) return;
    if (REDUCED.matches) return;
    for (const b of (root || document).querySelectorAll(SEL)) {
      if (b.getAttribute('data-moojo-role')) continue;
      observe(b);
    }
    schedule();
  }

  function init(root) {
    initB2();
    initSolo(root);
  }

  function destroy(root) {
    const scope = root || document;
    [...observed].forEach((el) => {
      if (scope.contains(el) || scope === document) forget(el);
    });
  }

  function figer() {
    b2Listening = false;
    [...observed].forEach(forget);
    bindScroll(false);
    figerB2();
  }

  REDUCED.addEventListener?.('change', () => {
    if (REDUCED.matches) figer();
    else window.moojoWheelInit();
  });

  document.addEventListener('shopify:section:load', (e) => window.moojoWheelInit(e.target));
  document.addEventListener('shopify:section:unload', (e) => destroy(e.target));
  document.addEventListener('shopify:section:reorder', () => { window.moojoWheelInit(); schedule(); });
  document.addEventListener('shopify:section:select', () => schedule());

  window.moojoWheelInit = (root) => { init(root); };
  window.moojoWheelDestroy = destroy;

  function demarrer() {
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer, { once: true });
  } else {
    demarrer();
  }
})();
